import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';

import { AreaOfInterest, BusinessCard, EmployeeCount, JobInformation } from '@planone/bau365/util-user-data';
import { CursorInterface } from '@planone/shared/util-api-cursor-handling';

import { Profession, SpecializationSynonyms, Synonym } from './data-access-user.models';

export const userProfileCompletionPageOpened = createAction('[User Profile Completion Page] Opened');

export const professionsPartialLoadSucceeded = createAction(
  '[Onboarding Tool] Professions partial load succeeded',
  props<{ professions: Profession[] } & CursorInterface>()
);

export const professionsLoadFailed = createAction(
  '[Onboarding Tool] Professions load failed',
  props<{ error: unknown }>()
);

export const synonymsLoadSucceeded = createAction(
  '[Onboarding Tool] Synonyms load succeeded',
  props<{ synonyms: SpecializationSynonyms[] }>()
);

export const synonymsLoadFailed = createAction('[Onboarding Tool] Synonyms load failed', props<{ error: unknown }>());

export const professionSpecializationSubmitted = createAction(
  '[User Profile Completion Page] Profession/Specialization submitted',
  props<{ profession: string; specialization: string; synonym?: Synonym }>()
);

export const professionSpecializationsSubmitSucceeded = createAction(
  '[User Service] Profession/Specialization submit succeeded',
  props<{ profession: string; specialization: string; synonym?: Synonym }>()
);

export const professionSpecializationsSubmitFailed = createAction(
  '[User Service] Profession/Specialization submit failed',
  props<{ error: unknown }>()
);

export const userProfilePageOpened = createAction('[User Profile Page] Opened');

export const userJobInformationSubmitted = createAction(
  '[User Job Page] Job information submitted',
  props<{ jobInformation: JobInformation }>()
);

export const userJobInformationSubmitSucceeded = createAction(
  '[User Service] Job information submit succeeded',
  props<{ jobInformation: JobInformation }>()
);

export const userJobInformationSubmitFailed = createAction(
  '[User Service] Job information submit failed',
  props<{ error: unknown }>()
);

export const userJobInformationLoadSucceeded = createAction(
  '[User Service] Job information load succeeded',
  props<{ jobInformation: JobInformation }>()
);

export const userJobInformationLoadFailed = createAction(
  '[User Service] Job information load failed',
  props<{ error: unknown }>()
);

export const loadEmployeeCountOptions = createAction('[User Job Page] Load employee count options');

export const loadEmployeeCountOptionsSuccess = createAction(
  '[User Job Page] Load employee count options success',
  props<{ employeeCountOptions: EmployeeCount[] }>()
);

export const loadEmployeeCountOptionsFailure = createAction(
  '[User Job Page] Load employee count options failure',
  props<{ error: unknown }>()
);

export const loadAreaOfInterestOptions = createAction('[User Job Page] Load area of interest options');

export const loadAreaOfInterestOptionsSuccess = createAction(
  '[User Job Page] Load area of interest options success',
  props<{ areaOfInterestOptions: AreaOfInterest[] }>()
);

export const loadAreaOfInterestOptionsFailure = createAction(
  '[User Job Page] Load area of interest options failure',
  props<{ error: unknown }>()
);

export const profileSubmitted = createAction(
  '[User Profile Page] Profile submitted',
  props<{ familyName: string; givenName: string }>()
);

export const userProfileSubmitSucceeded = createAction(
  '[User Service] User Profile submit succeeded',
  props<{ title: string; shortDescription: string }>()
);

export const keycloakProfileSubmitSucceeded = createAction(
  '[Keycloak API] Keycloak Profile submit succeeded',
  props<{ familyName: string; givenName: string }>()
);

export const userProfileSubmitFailed = createAction(
  '[User Service] User Profile submit failed',
  props<{ error: unknown }>()
);

export const keycloakProfileSubmitFailed = createAction(
  '[Keycloak API] Keycloak Profile submit failed',
  props<{ error: unknown }>()
);

export const userProfileLoadedSucceeded = createAction(
  '[User Service] User Profile load succeeded',
  props<{ title: string; shortDescription: string }>()
);

export const userProfileLoadedFailed = createAction(
  '[User Service] User Profile load failed',
  props<{ error: unknown }>()
);

export const userProfessionalInformationLoadSucceeded = createAction(
  '[User Service] User professional information load succeeded',
  props<{ professions: string[]; specializations: string[]; synonyms?: Synonym[] }>()
);

export const userProfessionalInformationLoadFailed = createAction(
  '[User Service] User professional information load failed',
  props<{ error: unknown }>()
);

export const userInterestsLoadSucceeded = createAction(
  '[User Service] User interests load succeeded',
  props<{ productGroups: string[]; topics: string[] }>()
);

export const userInterestsLoadFailed = createAction(
  '[User Service] User interests load failed',
  props<{ error: unknown }>()
);

export const userIsLoggedIn = createAction('[Keycloak API] User is logged in');

export const userRolesLoaded = createAction('[Keycloak API] User roles loaded', props<{ userRoles: string[] }>());

export const savedProfessionSpecializationLoaded = createAction(
  '[User Service] Profession/Specialization loaded',
  props<{ profession: string; specialization: string; synonyms?: Synonym }>()
);

export const savedProfessionSpecializationNotFound = createAction('[User Service] Profession/Specialization not found');

export const newsletterSubscriptionSubmitted = createAction(
  '[User Service] Newsletter signup submitted',
  props<{ email: string }>()
);

export const newsletterSubscriptionSucceeded = createAction(
  '[User Service] Newsletter subscription succeeded',
  props<{ email: string }>()
);

export const newsletterSubscriptionFailed = createAction('[User Service] Newsletter subscription failed');

export const deleteAccountSubmitted = createAction(
  '[User Profile Page] Delete account submit',
  props<{ predefinedReason: string; otherReason: string }>()
);

export const deleteAccountSubmitSucceeded = createAction('[User Service] Delete account submit succeeded');

export const deleteAccountSubmitFailed = createAction(
  '[User Service] Delete Account submit failed',
  props<{ error: unknown }>()
);

export const showBusinessCard = createAction('[User Profile Page] Show Business Card');
export const userBusinessCardPageOpened = createAction('[User Business Card Page] Opened');
export const startBusinessCardCreation = createAction('[User Business Card Page] Start Business Card creation');
export const changeBusinessCardDetails = createAction('[User Business Card Page] Change Business Card details');

export const loadBusinessCard = createAction('[User Service] Load business card');
export const loadBusinessCardSuccess = createAction(
  '[User Service] Load business card success',
  props<{ businessCard: BusinessCard | undefined }>()
);
export const loadBusinessCardFailure = createAction(
  '[User Service] Load business card failure',
  props<{ error: unknown }>()
);

export const createBusinessCard = createAction('[User Profile Page] Create business card');
export const createBusinessCardSuccess = createAction(
  '[User Profile Page] Create business card success',
  props<{ businessCard: BusinessCard }>()
);
export const createBusinessCardFailure = createAction(
  '[User Profile Page] Create business card failure',
  props<{ error: unknown }>()
);

export const resetBusinessCard = createAction('[User Profile Page] Reset business card');
export const resetBusinessCardSuccess = createAction(
  '[User Profile Page] Reset business card success',
  props<{ businessCard: BusinessCard }>()
);
export const resetBusinessCardFailure = createAction(
  '[User Profile Page] Reset business card failure',
  props<{ error: unknown }>()
);

export const downloadBusinessCardQrCode = createAction(
  '[User Profile Page] Download business card QR Code',
  props<{ canvas: HTMLCanvasElement }>()
);

export const userProfileImageSubmitted = createAction(
  '[User Job Page] Profile image submitted',
  props<{ profileImage: File }>()
);

export const userProfileImageSubmitSucceeded = createAction('[User Job Page] Profile image submit succeeded');

export const userProfileImageSubmitFailed = createAction(
  '[User Job Page] Profile image submit failed',
  props<{ error: string }>()
);
export const userProfileImageDelete = createAction('[User Job Page] Delete profile image');
export const userProfileImageDeleteSucceeded = createAction('[User Job Page] Delete profile image succeeded');
export const userProfileImageDeleteFailed = createAction(
  '[User Job Page] Delete profile image failed',
  props<{ error: unknown }>()
);

export const sendBusinessCardButtonActions = createActionGroup({
  source: 'Send Business Card Button',
  events: {
    'Init button': emptyProps(),
    'Send business card requested': props<{ fairId: string; exhibitorId: string }>(),
    'Send business card rejected': emptyProps(),
    'Send business card': props<{ fairId: string; exhibitorId: string }>(),
    'Send business card successful': props<{ exhibitorId: string }>(),
    'Send business card failed': props<{ error: unknown }>(),
  },
});
